import { type FeedSuggestion, feedRecordKey } from '@parcel/types';
import { Button, Skeleton, cn } from '@parcel/ui';
import { Building2, ChevronLeft, ChevronRight, Loader2, Tag, X } from 'lucide-react';
import * as React from 'react';
import { ApiError } from '../../lib/api.js';
import {
  useSuggestionMutations,
  useSuggestionWritesPending,
  useSuggestions,
} from '../../lib/feed.js';
import { logoUrl } from '../../lib/logo.js';
import { useWatchMutations } from '../../lib/workspace-views.js';
import { EntityAvatar } from '../entity-drawer/entity-drawer-shell.js';

/** The best-scored records nobody follows yet, as a row of cards; Follow adds one to the Feed, × skips it. */
export function FeedSuggested({
  workspaceId,
  onOpen,
  empty,
}: {
  workspaceId: string | undefined;
  onOpen: (row: FeedSuggestion) => void;
  /** What to show when there is nothing to suggest. */
  empty: React.ReactNode;
}) {
  // Followed here this visit: the card stays with an Unfollow, and leaves Suggested on the next load.
  const [followedKeys, setFollowedKeys] = React.useState<ReadonlySet<string>>(new Set());
  const suggestions = useSuggestions(workspaceId, followedKeys);
  const { skip } = useSuggestionMutations(workspaceId);
  const followProjects = useWatchMutations(workspaceId, 'project');
  const followAccounts = useWatchMutations(workspaceId, 'account');
  const [all, setAll] = React.useState(false);
  const [pendingKeys, setPendingKeys] = React.useState<ReadonlySet<string>>(new Set());
  const mark = (
    set: React.Dispatch<React.SetStateAction<ReadonlySet<string>>>,
    key: string,
    on: boolean,
  ) =>
    set((current) => {
      const next = new Set(current);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  const rail = React.useRef<HTMLOListElement>(null);
  // Offset pages can overlap when new activity moves a record between reads; each card shows once.
  const seen = new Set<string>();
  const rows = (suggestions.data?.pages ?? [])
    .flatMap((page) => page.rows)
    .filter((row) => {
      const key = feedRecordKey(row);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  // Arrows only where there is more to scroll to.
  const [edges, setEdges] = React.useState({ left: false, right: false });
  const measure = React.useCallback(() => {
    const el = rail.current;
    if (!el) return;
    setEdges({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the rail mounts after the first load and changes with the cards.
  React.useEffect(() => {
    const el = rail.current;
    if (!el) return;
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, rows.length, all]);
  // Every loaded card skipped while more wait on the server: read the next page rather than vanish,
  // once the skips have landed (a read before would bring them back), and never again after a failed read.
  const writing = useSuggestionWritesPending(workspaceId);
  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = suggestions;
  React.useEffect(() => {
    if (rows.length > 0 || !hasNextPage || isFetchingNextPage || isFetchNextPageError || writing)
      return;
    void fetchNextPage();
  }, [rows.length, hasNextPage, isFetchingNextPage, isFetchNextPageError, writing, fetchNextPage]);

  // No workspace (it failed to load, or the app runs without one): the query never starts.
  if (!workspaceId) return <>{empty}</>;
  if (suggestions.isPending) return <SuggestedSkeleton />;
  // An API without the route yet (the web deploys first) answers 404: nothing to suggest.
  if (suggestions.error instanceof ApiError && suggestions.error.status === 404)
    return <>{empty}</>;
  if (suggestions.isError) {
    return (
      <p className="text-sm text-muted-foreground">
        Couldn't load suggestions.{' '}
        <button
          type="button"
          className="underline underline-offset-2"
          onClick={() => void suggestions.refetch()}
        >
          Retry
        </button>
      </p>
    );
  }
  if (rows.length === 0) return hasNextPage ? <SuggestedSkeleton /> : <>{empty}</>;

  // mutateAsync, not per-call callbacks: a second quick follow detaches the first call's callbacks.
  const toggleFollow = (row: FeedSuggestion) => {
    const key = feedRecordKey(row);
    if (pendingKeys.has(key)) return;
    const hooks = row.dataset === 'project' ? followProjects : followAccounts;
    const following = followedKeys.has(key);
    mark(setPendingKeys, key, true);
    const write = following
      ? hooks.unfollow.mutateAsync([row.record_id])
      : hooks.follow.mutateAsync([row.record_id]);
    void write
      .then((result) => {
        // A 200 can still refuse the record; the follow hook says why and the card keeps its state.
        if (result.results.every((item) => item.status === 'ok')) {
          mark(setFollowedKeys, key, !following);
        }
      })
      .catch(() => {})
      .finally(() => mark(setPendingKeys, key, false));
  };
  // A followed card cannot be skipped (the server refuses); its Unfollow is the way out.
  const pass = (row: FeedSuggestion) => {
    if (followedKeys.has(feedRecordKey(row))) return;
    skip.mutate({ dataset: row.dataset, record_id: row.record_id });
  };
  const scroll = (direction: 1 | -1) =>
    rail.current?.scrollBy({ left: direction * 2 * 192, behavior: 'smooth' });

  return (
    <section aria-label="Suggested to follow" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">Suggested for you</h2>
        {all || edges.left || edges.right || suggestions.hasNextPage ? (
          <Button
            variant="link"
            size="sm"
            className="h-auto px-0"
            onClick={() => setAll((value) => !value)}
          >
            {all ? 'Show fewer' : 'See all'}
          </Button>
        ) : null}
      </div>
      <div className="relative">
        <ol
          ref={rail}
          onScroll={measure}
          aria-label="Suggested to follow"
          className={cn(
            all
              ? 'grid grid-cols-[repeat(auto-fill,minmax(176px,1fr))] gap-3'
              : 'flex snap-x gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          )}
        >
          {rows.map((row) => (
            <SuggestionCard
              key={feedRecordKey(row)}
              row={row}
              followed={followedKeys.has(feedRecordKey(row))}
              pending={pendingKeys.has(feedRecordKey(row))}
              onOpen={onOpen}
              onFollow={toggleFollow}
              onSkip={pass}
            />
          ))}
        </ol>
        {!all && edges.left ? <RailArrow side="left" onClick={() => scroll(-1)} /> : null}
        {!all && edges.right ? <RailArrow side="right" onClick={() => scroll(1)} /> : null}
      </div>
      {all && suggestions.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            disabled={suggestions.isFetchingNextPage}
            onClick={() => void suggestions.fetchNextPage()}
          >
            {suggestions.isFetchingNextPage ? 'Loading…' : 'Show more'}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function RailArrow({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={side === 'left' ? 'Earlier suggestions' : 'More suggestions'}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 z-10 flex size-7 -translate-y-1/2 items-center justify-center rounded-full border bg-background/95 shadow-sm hover:bg-accent',
        side === 'left' ? '-left-3' : '-right-3',
      )}
    >
      <Icon className="size-4" />
    </button>
  );
}

/** One suggestion: who or what it is, Follow (Unfollow once followed), and × to skip it for the workspace. */
function SuggestionCard({
  row,
  followed,
  pending,
  onOpen,
  onFollow,
  onSkip,
}: {
  row: FeedSuggestion;
  followed: boolean;
  pending: boolean;
  onOpen: (row: FeedSuggestion) => void;
  onFollow: (row: FeedSuggestion) => void;
  onSkip: (row: FeedSuggestion) => void;
}) {
  const isAccount = row.dataset === 'account';
  return (
    <li
      // biome-ignore lint/a11y/noNoninteractiveTabindex: a focused suggestion takes f, s and Enter.
      tabIndex={0}
      aria-keyshortcuts="f s Enter"
      data-testid="feed-suggestion"
      className="relative flex w-44 shrink-0 snap-start flex-col rounded-xl border bg-card text-center outline-none focus-visible:ring-1 focus-visible:ring-brand/40"
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.metaKey || event.ctrlKey || event.altKey) return;
        const action =
          event.key === 'f'
            ? onFollow
            : event.key === 's'
              ? onSkip
              : event.key === 'Enter'
                ? onOpen
                : null;
        if (!action) return;
        // Ahead of the Feed's own keys, which read s as mark-read.
        event.preventDefault();
        if (action === onSkip && followed) return;
        // Focus moves on before s takes the card away, so the next key stays here.
        if (action === onSkip) {
          const next =
            event.currentTarget.nextElementSibling ?? event.currentTarget.previousElementSibling;
          if (next instanceof HTMLElement) next.focus();
        }
        action(row);
      }}
    >
      <span className="absolute left-3 top-2.5 inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {isAccount ? (
          <Building2 className="size-3" aria-hidden />
        ) : (
          <Tag className="size-3" aria-hidden />
        )}
        {isAccount ? 'Account' : 'Project'}
      </span>
      {followed ? null : (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1 size-7 text-muted-foreground"
          aria-label="Skip"
          onClick={() => onSkip(row)}
        >
          <X className="size-3.5" />
        </Button>
      )}
      <button
        type="button"
        className="flex flex-1 flex-col items-center gap-2 px-3 pb-3 pt-8"
        onClick={() => onOpen(row)}
      >
        <EntityAvatar
          src={logoUrl(row.logo_domain ?? (isAccount ? row.website : null))}
          alt={row.name}
          fallbackIcon={isAccount ? Building2 : Tag}
        />
        <span className="w-full truncate text-sm font-semibold">{row.name}</span>
        <span className="w-full truncate text-xs text-muted-foreground">
          {row.subtitle ?? (isAccount ? 'Firm' : 'Project')}
        </span>
      </button>
      <div className="border-t p-2">
        <Button
          size="sm"
          variant={followed ? 'outline' : 'secondary'}
          className="h-8 w-full transition-colors"
          aria-pressed={followed}
          aria-busy={pending}
          disabled={pending}
          onClick={() => onFollow(row)}
        >
          <span
            key={pending ? 'pending' : followed ? 'unfollow' : 'follow'}
            className="inline-flex items-center gap-1.5 animate-in fade-in zoom-in-95 duration-200"
          >
            {pending ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
            {pending ? (followed ? 'Unfollowing' : 'Following') : followed ? 'Unfollow' : 'Follow'}
          </span>
        </Button>
      </div>
    </li>
  );
}

/** The card row's shape while suggestions load. */
export function SuggestedSkeleton() {
  return (
    <section aria-label="Loading suggestions" aria-busy="true" className="space-y-3">
      <Skeleton className="h-4 w-32" />
      <div className="flex gap-3 overflow-hidden">
        {[0, 1, 2, 3, 4].map((card) => (
          <div
            key={card}
            className="flex w-44 shrink-0 flex-col items-center gap-2 rounded-xl border bg-card px-3 pb-2 pt-8"
          >
            <Skeleton className="size-12 rounded-full" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="mt-3 h-8 w-full" />
          </div>
        ))}
      </div>
    </section>
  );
}
